/**
 * System Defaults API
 * Fetches system default values from API endpoints
 */

import { apiClient } from './client';

// Map field names to their system config endpoints
// Note: paths are relative to apiClient baseURL (/v1/admin)
const FIELD_ENDPOINTS: Record<string, string> = {
  // Memory prompts
  'memory.prompts.user_memory_extraction_prompt': '/system/mem0/user-memory-extraction-prompt',
  'memory.prompts.agent_memory_extraction_prompt': '/system/mem0/agent-memory-extraction-prompt',
  'memory.prompts.memory_update_prompt': '/system/mem0/memory-update-prompt',
  // Memory categories
  'memory.categories.user_custom_categories': '/system/mem0/user-memory-categories',
  'memory.categories.agent_custom_categories': '/system/mem0/agent-memory-categories',
  // First message instruction
  'first_message_custom_instruction': '/system/first-message',
};

// Map field names to their value extractors
type ValueExtractor = (data: any) => any;

const VALUE_EXTRACTORS: Record<string, ValueExtractor> = {
  'memory.prompts.user_memory_extraction_prompt': (data) => data.prompt || data.user_memory_extraction_prompt,
  'memory.prompts.agent_memory_extraction_prompt': (data) => data.prompt || data.agent_memory_extraction_prompt,
  'memory.prompts.memory_update_prompt': (data) => data.prompt || data.memory_update_prompt,
  'memory.categories.user_custom_categories': (data) => data.categories || data.user_memory_categories,
  'memory.categories.agent_custom_categories': (data) => data.categories || data.agent_memory_categories,
  'first_message_custom_instruction': (data) => data.instruction,
};

export interface SystemDefaultResult {
  success: boolean;
  value?: any;
  error?: string;
}

/**
 * Fetch system default value for a field
 * @param fieldName The field name (e.g., 'memory.prompts.user_memory_extraction_prompt')
 * @returns The system default value or error
 */
export async function fetchSystemDefault(fieldName: string): Promise<SystemDefaultResult> {
  const endpoint = FIELD_ENDPOINTS[fieldName];

  if (!endpoint) {
    return {
      success: false,
      error: `No system default endpoint configured for ${fieldName}`,
    };
  }

  try {
    const response = await apiClient.get<any>(endpoint);
    const data = response.data;

    const extractor = VALUE_EXTRACTORS[fieldName];
    const value = extractor ? extractor(data) : data;

    if (value !== undefined && value !== null) {
      return {
        success: true,
        value,
      };
    } else {
      return {
        success: false,
        error: 'No system default configured',
      };
    }
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to load system default',
    };
  }
}

/**
 * Check if a field has a system default endpoint
 */
export function hasSystemDefault(fieldName: string): boolean {
  return fieldName in FIELD_ENDPOINTS;
}
